import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  BadgeCheck,
  Ban,
  BellOff,
  CalendarClock,
  Check,
  CheckCheck,
  Download,
  LifeBuoy,
  MessageSquareText,
  PencilLine,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { ERRORS, ROUTES } from '@/constants';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { cn } from '@/lib/utils';
import { useMarkRead, useNotifications, useUnreadCount } from './queries';
import type { Notification, NotificationKind, NotificationTab } from './types';

/**
 * What each kind looks like, and where opening one takes you.
 *
 * The portal has no case pages, so a notification about a case goes to the
 * screen that carries it here: a dentist's decision or note to Review Links,
 * an issue to Issues, the licence and the installer to the dashboard.
 */
const KIND: Record<NotificationKind, { icon: LucideIcon; tone: string; to: string }> = {
  crown_approved: {
    icon: BadgeCheck,
    tone: 'text-pill-positive-foreground',
    to: ROUTES.reviewLinks,
  },
  crown_changes_requested: {
    icon: PencilLine,
    tone: 'text-pill-caution-foreground',
    to: ROUTES.reviewLinks,
  },
  crown_rejected: { icon: XCircle, tone: 'text-pill-negative-foreground', to: ROUTES.reviewLinks },
  review_note_added: {
    icon: MessageSquareText,
    tone: 'text-brand-600',
    to: ROUTES.reviewLinks,
  },
  issue_updated: { icon: LifeBuoy, tone: 'text-brand-600', to: ROUTES.issues },
  version_available: { icon: Download, tone: 'text-brand-600', to: ROUTES.dashboard },
  license_expiring: {
    icon: CalendarClock,
    tone: 'text-pill-caution-foreground',
    to: ROUTES.dashboard,
  },
  seat_revoked: { icon: Ban, tone: 'text-pill-negative-foreground', to: ROUTES.dashboard },
};

/** "Just now", "5 min ago", "3 h ago", "Yesterday", then the date. */
function when(iso: string, now: number): string {
  const minutes = Math.floor((now - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)} h ago`;
  if (hours < 48) return 'Yesterday';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const TABS: { key: NotificationTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
];

/**
 * Everything that has happened for the signed-in user, newest first.
 *
 * Nothing is marked read just by opening the page: an unread item stays
 * unread until it is opened or marked, so a glance at the list does not
 * silently clear what somebody has not actually looked at yet.
 */
export function Notifications() {
  const [tab, setTab] = useState<NotificationTab>('all');
  const list = useNotifications(tab);
  const unread = useUnreadCount();
  const markRead = useMarkRead();
  const navigate = useNavigate();

  // One clock reading per fetch, so "5 min ago" does not drift between rows.
  const now = list.dataUpdatedAt;
  const items = list.data?.pages.flatMap((page) => page.data) ?? [];
  const unreadTotal = unread.data ?? 0;

  const open = (notification: Notification) => {
    if (notification.readAt === null) markRead.mutate([notification.id]);
    void navigate(KIND[notification.kind].to);
  };

  let body: ReactNode;
  if (list.isPending) {
    body = (
      <div className="space-y-2 p-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 w-full bg-muted" />
        ))}
      </div>
    );
  } else if (list.isError) {
    body = (
      <Message
        title="Notifications could not be loaded"
        detail={list.error instanceof Error ? list.error.message : ERRORS.generic}
      />
    );
  } else if (items.length === 0) {
    body =
      tab === 'unread' ? (
        <Message title="You're all caught up" detail="Nothing unread." />
      ) : (
        <Message
          title="No notifications yet"
          detail="Dentist decisions, issue updates and licence reminders appear here."
        />
      );
  } else {
    body = (
      <ul className="divide-y divide-border">
        {items.map((notification) => (
          <NotificationRow
            key={notification.id}
            notification={notification}
            now={now}
            onOpen={open}
            onMarkRead={(id) => {
              markRead.mutate([id]);
            }}
            marking={markRead.isPending}
          />
        ))}
      </ul>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div role="tablist" aria-label="Filter notifications" className="flex gap-1">
          {TABS.map(({ key, label }) => {
            const selected = key === tab;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => {
                  setTab(key);
                }}
                className={cn(
                  'flex h-8 items-center gap-1.5 rounded-lg px-3 text-[12.5px] font-medium transition-colors',
                  selected
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {label}
                {key === 'unread' && unreadTotal > 0 && (
                  <span
                    className={cn(
                      'rounded-full px-1.5 text-[10.5px] tabular-nums',
                      selected ? 'bg-white/20' : 'bg-muted',
                    )}
                  >
                    {unreadTotal}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          disabled={unreadTotal === 0 || markRead.isPending}
          onClick={() => {
            markRead.mutate(undefined);
          }}
        >
          <CheckCheck className="size-4" />
          Mark all as read
        </Button>
      </div>

      <div className="overflow-hidden rounded-[14px] border border-border bg-card">{body}</div>

      {list.hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            disabled={list.isFetchingNextPage}
            onClick={() => {
              void list.fetchNextPage();
            }}
          >
            {list.isFetchingNextPage ? 'Loading…' : 'Show older'}
          </Button>
        </div>
      )}
    </div>
  );
}

function NotificationRow({
  notification,
  now,
  onOpen,
  onMarkRead,
  marking,
}: {
  notification: Notification;
  now: number;
  onOpen: (notification: Notification) => void;
  onMarkRead: (id: string) => void;
  marking: boolean;
}) {
  const kind = KIND[notification.kind];
  const Icon = kind.icon;
  const unread = notification.readAt === null;

  return (
    <li className={cn('flex items-start gap-3 px-4 py-3.5', unread && 'bg-brand-50/40')}>
      <button
        type="button"
        onClick={() => {
          onOpen(notification);
        }}
        className="flex min-w-0 flex-1 items-start gap-3 rounded-md text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <span
          className={cn(
            'mt-0.5 flex size-8 flex-none items-center justify-center rounded-lg bg-muted',
            kind.tone,
          )}
          aria-hidden="true"
        >
          <Icon size={16} strokeWidth={1.9} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            {unread && (
              <span className="size-2 flex-none rounded-full bg-primary" aria-label="Unread" />
            )}
            <span className={cn('truncate text-[13px]', unread ? 'font-semibold' : 'font-medium')}>
              {notification.title}
            </span>
          </span>
          {notification.body && (
            <span className="mt-0.5 block text-[12.5px] leading-snug text-muted-foreground">
              {notification.body}
            </span>
          )}
          <span className="mt-1 block text-[11.5px] text-muted-foreground">
            {when(notification.createdAt, now)}
          </span>
        </span>
      </button>

      {unread && (
        <Button
          variant="ghost"
          size="sm"
          disabled={marking}
          onClick={() => {
            onMarkRead(notification.id);
          }}
          title="Mark as read"
        >
          <Check className="size-4" />
          <span className="hidden sm:inline">Mark read</span>
        </Button>
      )}
    </li>
  );
}

function Message({ title, detail }: { title: string; detail: ReactNode }) {
  return (
    <div className="px-4 py-14 text-center">
      <BellOff className="mx-auto mb-2 size-6 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm font-medium">{title}</p>
      <p className="mt-1 text-[12.5px] text-muted-foreground">{detail}</p>
    </div>
  );
}
